/**
 * Обратная связь · окно 5 запуска — тексты, раскладка и СБОР КОНТЕКСТА.
 *
 * Девятая часть витрины с настоящей логикой. Здесь нет ни одной цифры, посчитанной
 * витриной: среднее и распределение приходят от ядра (`core/feedback.py`), правило
 * «пора ли спрашивать» — тоже. Экран решает только КАК это показать.
 *
 * ⭐ ГЛАВНОЕ, ради чего модуль отдельный: КОНТЕКСТ ЗАЯВКИ СОБИРАЕТ ПРИЛОЖЕНИЕ.
 *   «У меня не работает» без подробностей бесполезно, а требовать их от человека
 *   нельзя — он не обязан знать, что такое «ступень 3» и «ответ сервера». Поэтому
 *   витрина всю дорогу тихо помнит последнее: экран, слово, задание, ошибку сервера,
 *   свою версию и устройство, — а человек пишет одну строку.
 *   ⛔ Набор полей ЗАКРЫТ и совпадает с `feedback.CONTEXT_FIELDS` в ядре: лишнее ядро
 *   всё равно отбросит, а молчаливое расхождение выглядело бы как «контекст пустой».
 *
 * ⭐ Второе: СРЕДНЕЕ ПОКАЗЫВАЕМ ВМЕСТЕ С РАСПРЕДЕЛЕНИЕМ. На малой выборке одна цифра
 *   врёт (один злой человек роняет 4.6 до 4.0), поэтому у сводки всегда есть полоски,
 *   а пока оценок мало — честная подпись об этом.
 *
 * Проверяется запуском: cd Lang/web && node tools/check-feedback.mjs
 */

/** Версия витрины — уезжает в контекст заявки. Меняется вместе с выкладкой. */
export const APP_VERSION = "w5.2026-08-18";

export const STARS_MIN = 1;
export const STARS_MAX = 5;
/** Предел длины. ⛔ Правда о нём — в ядре (`feedback.TEXT_MAX`), тут только поле ввода. */
export const TEXT_MAX = 1000;

export type Status = "new" | "doing" | "done";

/** Подписи статусов. Приезжают и от API — здесь запасные, чтобы экран не пустовал. */
export const STATUS_LABEL: Record<Status, string> = {
  new: "принято",
  doing: "чиним",
  done: "готово",
};

/** Что статус значит ДЛЯ ЧЕЛОВЕКА: «принято» без объяснения читается как «отписка». */
export const STATUS_HINT: Record<Status, string> = {
  new: "Заявку видно, скоро посмотрим",
  doing: "Разбираемся с этим прямо сейчас",
  done: "Починили — расскажем, если понадобится проверить",
};

export const STATUS_ORDER: Status[] = ["new", "doing", "done"];

/** Слово под звёздами: цифра без слова ничего не подсказывает. */
export const STAR_WORD = ["", "плохо", "так себе", "нормально", "хорошо", "отлично"];

export const ASK_TITLE = "Как тебе Lang?";
export const ASK_HINT = "Ты занимаешься уже неделю — скажи, как оно. " +
  "Спросим ещё раз не раньше чем через три месяца.";
export const ASK_WISH = "Что добавить или починить? (по желанию)";
export const ASK_THANKS = "Спасибо — прочитаем всё.";
export const ASK_LATER = "Не сейчас";

export const REPORT_TITLE = "Что-то не работает?";
export const REPORT_HINT = "Напиши одной строкой, что случилось. Экран, слово и ответ " +
  "сервера приложим сами — вводить ничего не нужно.";
export const REPORT_SENT = "Заявка ушла. Ответим статусом прямо здесь и сообщением в Telegram.";

/** ⚠ Мало оценок — среднее ещё шумит. Ниже этого числа подписываем честно. */
export const NOISY_BELOW = 5;

/* ── падежи ────────────────────────────────────────────────────────────────── */

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export function ratingsWord(n: number): string {
  return `${n} ${plural(n, "оценка", "оценки", "оценок")}`;
}

export function ticketsWord(n: number): string {
  return `${n} ${plural(n, "заявка", "заявки", "заявок")}`;
}

export function daysWord(n: number): string {
  return `${n} ${plural(n, "день", "дня", "дней")}`;
}

/* ── сводка оценок ─────────────────────────────────────────────────────────── */

export interface Summary {
  count: number;
  average: number | null;
  distribution: Record<string, number>;
}

/** «4,3 из 5» — запятая, как принято по-русски. Оценок нет — так и говорим. */
export function averageLabel(s: Summary): string {
  if (!s.count || s.average === null) return "оценок пока нет";
  return `${s.average.toFixed(1).replace(".", ",")} из ${STARS_MAX}`;
}

/** Честная приписка про шум. ⛔ Не прячем среднее — просто не даём ему врать. */
export function noisyNote(s: Summary): string | null {
  if (!s.count) return null;
  return s.count < NOISY_BELOW
    ? `Оценок мало (${ratingsWord(s.count)}) — среднее ещё скачет, смотри распределение`
    : null;
}

export interface DistRow { stars: number; count: number; share: number }

/**
 * Полоски распределения. Доли дают РОВНО 100% (последняя ненулевая досчитывается
 * вычитанием) — тот же закон, что в Ф2.4: округление иначе даёт 99 или 101.
 */
export function distributionRows(s: Summary): DistRow[] {
  const rows: DistRow[] = [];
  for (let stars = STARS_MAX; stars >= STARS_MIN; stars--) {
    rows.push({ stars, count: Number(s.distribution?.[String(stars)] ?? 0), share: 0 });
  }
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  if (!total) return rows;
  let used = 0;
  let last = -1;
  rows.forEach((r, i) => { if (r.count) last = i; });
  rows.forEach((r, i) => {
    if (!r.count) return;
    if (i === last) { r.share = 100 - used; return; }
    r.share = Math.round((r.count / total) * 100);
    used += r.share;
  });
  return rows;
}

/* ── заявка ────────────────────────────────────────────────────────────────── */

export function trimTicket(text: string): string {
  return String(text ?? "").trim().slice(0, TEXT_MAX);
}

/** Пустое не отправляем: ядро такую заявку не примет, а человек увидит ошибку зря. */
export function ticketValid(text: string): boolean {
  return trimTicket(text).length > 0;
}

export function statusStep(status: string): number {
  const i = STATUS_ORDER.indexOf(status as Status);
  return i < 0 ? 1 : i + 1;
}

export function statusLabel(status: string): string {
  return STATUS_LABEL[status as Status] ?? status;
}

/* ── КОНТЕКСТ: приложение помнит за человека ───────────────────────────────── */

/** ⛔ Закрытый список — совпадает с `feedback.CONTEXT_FIELDS` в ядре. */
export const CONTEXT_FIELDS = ["screen", "word", "task", "server", "version",
                               "device", "client"] as const;

export type Context = Partial<Record<(typeof CONTEXT_FIELDS)[number], string>>;

/**
 * Сколько живёт запомненная беда. ⭐ Не «до следующего успешного запроса»: человек
 * идёт писать заявку ИЗ настроек, а по дороге приложение успевает сходить за `/me` —
 * и самая ценная строка контекста стёрлась бы ровно перед отправкой (поймано при сборке).
 * Десять минут — столько, за сколько беда ещё та же самая.
 */
export const ERROR_TTL_MS = 10 * 60 * 1000;

/**
 * ⛔ Экраны, которые НЕ запоминаются: заявку пишут из настроек, и строка «экран:
 * настройки» не помогает никому. Полезен тот экран, где человек был до этого.
 */
const SKIP_SCREENS = ["settings", "admin"];

const memory: Context & { serverAt?: number } = { version: APP_VERSION };

/** Устройство и клиент Telegram — ставятся один раз на старте (`main.ts`). */
export function noteClient(device: string, client: string): void {
  memory.device = device || undefined;
  memory.client = client || undefined;
}

export function noteScreen(screen: string): void {
  if (!screen || SKIP_SCREENS.includes(screen)) return;
  memory.screen = screen;
}

/** Слово и задание — ставит экран занятия при каждой карточке (одна строка). */
export function noteCard(word: string | null, task: string | null): void {
  memory.word = word || undefined;
  memory.task = task || undefined;
}

/**
 * Последний ответ сервера. ⭐ Пишем ТОЛЬКО неудачи: успешный запрос человеку не
 * интересен, а вот «500 на /answer» — ровно то, чего он сам никогда не расскажет.
 */
export function noteServerError(status: number, path: string, message: string,
                                now = Date.now()): void {
  const code = status ? String(status) : "нет связи";
  memory.server = `${code} · ${path}${message ? ` · ${message}` : ""}`.slice(0, 400);
  memory.serverAt = now;
}

/** Заявка ушла — беду забываем: следующая должна описывать следующую поломку. */
export function forgetServerError(): void {
  memory.server = undefined;
  memory.serverAt = undefined;
}

/** Снимок контекста для заявки. ⛔ Пустые поля не отправляем — мусор в базе не нужен. */
export function snapshot(now = Date.now()): Context {
  const out: Context = {};
  for (const key of CONTEXT_FIELDS) {
    // Протухшую беду не прикладываем: «500 на /answer» недельной давности увёл бы
    // владельца чинить не то, что сломалось сегодня.
    if (key === "server" && memory.serverAt && now - memory.serverAt > ERROR_TTL_MS) continue;
    const value = memory[key];
    if (value) out[key] = String(value).slice(0, 400);
  }
  return out;
}

/** Что показать человеку под формой: он вправе видеть, что именно мы приложим. */
export function describeContext(ctx: Context): string[] {
  const names: Record<string, string> = {
    screen: "экран", word: "слово", task: "задание", server: "ответ сервера",
    version: "версия", device: "устройство", client: "Telegram",
  };
  return CONTEXT_FIELDS.filter((k) => ctx[k]).map((k) => `${names[k]}: ${ctx[k]}`);
}
